import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ApiError } from '../../lib/api';
import { deplacements, NOMS_PRODUITS, type EtatDeplacement, type ManqueDeplacement } from '../../lib/dossiers';

const date = (iso: string) => new Date(iso).toLocaleDateString('fr-FR');

/**
 * « Changer ce dossier d'espace » (29/10/2026, lot 3).
 *
 * Un espace = un payeur : déplacer un dossier, c'est changer qui le paie. Le
 * déplacement prend effet à l'échéance de l'espace actuel, sans prorata. Si
 * l'espace d'arrivée n'a pas de formule pour l'un des produits du dossier, on
 * propose d'y souscrire — même parcours : société facturée, puis paiement.
 */
export function DeplacementDossier({ companyId }: { companyId: string }) {
  const [etat, setEtat] = useState<EtatDeplacement | null>(null);
  const [cible, setCible] = useState('');
  const [manques, setManques] = useState<{ spaceId: string; liste: ManqueDeplacement[] } | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);
  const [succes, setSucces] = useState<string | null>(null);
  const [envoi, setEnvoi] = useState(false);

  const charger = useCallback(async () => {
    try {
      const e = await deplacements.lire(companyId);
      setEtat(e);
      setCible((c) => c || e.destinations[0]?.id || '');
    } catch {
      setEtat(null);
    }
  }, [companyId]);

  useEffect(() => {
    void charger();
  }, [charger]);

  // Les produits y renvoient par `#deplacement` (« Changer le dossier d'espace »).
  useEffect(() => {
    if (etat && window.location.hash === '#deplacement') {
      document.getElementById('q-deplacement')?.scrollIntoView?.({ block: 'start' });
    }
  }, [etat]);

  if (!etat || (!etat.enCours && (!etat.gestionnaire || etat.destinations.length === 0))) return null;

  const programmer = async () => {
    setEnvoi(true);
    setErreur(null);
    setSucces(null);
    setManques(null);
    try {
      const r = await deplacements.programmer(companyId, cible);
      setSucces(`Déplacement vers « ${r.vers.name} » programmé pour le ${date(r.effectiveAt)}.`);
      await charger();
    } catch (e) {
      if (e instanceof ApiError && e.code === 'ESPACE_NON_COUVERT') {
        setManques({ spaceId: cible, liste: (e.details?.manques as ManqueDeplacement[]) ?? [] });
      } else {
        setErreur(e instanceof ApiError ? e.message : 'Le déplacement n’a pas pu être programmé.');
      }
    } finally {
      setEnvoi(false);
    }
  };

  const annuler = async () => {
    setEnvoi(true);
    setErreur(null);
    try {
      await deplacements.annuler(companyId);
      setSucces('Déplacement annulé : le dossier reste dans son espace.');
      await charger();
    } catch (e) {
      setErreur(e instanceof ApiError ? e.message : 'Le déplacement n’a pas pu être annulé.');
    } finally {
      setEnvoi(false);
    }
  };

  return (
    <section id="q-deplacement" className="dos-recap" aria-labelledby="dos-deplacement">
      <h2 id="dos-deplacement" className="dos-section">
        Changer d’espace
      </h2>

      {etat.enCours ? (
        <>
          <p>
            Ce dossier passera dans l’espace <strong>{etat.enCours.toSpace.name}</strong> le{' '}
            <strong>{date(etat.enCours.effectiveAt)}</strong>, à l’échéance de l’abonnement de l’espace «{' '}
            {etat.dossier.espace} ». D’ici là, rien ne change.
          </p>
          <div className="dos-actions">
            <button type="button" className="btn btn-secondaire" disabled={envoi} onClick={() => void annuler()}>
              Annuler le déplacement
            </button>
          </div>
        </>
      ) : (
        <>
          <p className="dos-aide">
            Le dossier sera facturé par l’espace d’arrivée. Le changement prend effet le {date(etat.echeance)}, à
            l’échéance de l’espace « {etat.dossier.espace} » : sans prorata ni remboursement, et rien ne change d’ici
            là.
          </p>
          <div className="dos-question">
            <label htmlFor="dos-deplacement-cible">Vers l’espace</label>
            <select id="dos-deplacement-cible" value={cible} onChange={(e) => setCible(e.target.value)}>
              {etat.destinations.map((d) => (
                <option key={d.id} value={d.id}>
                  {d.name}
                </option>
              ))}
            </select>
          </div>
          <div className="dos-actions">
            <button type="button" className="btn" disabled={envoi || !cible} onClick={() => void programmer()}>
              {envoi ? 'Un instant…' : 'Programmer le déplacement'}
            </button>
          </div>
        </>
      )}

      {manques ? (
        <div className="dos-recap" role="alert">
          <p>L’espace choisi ne peut pas encore accueillir ce dossier :</p>
          <ul>
            {manques.liste.map((m) => (
              <li key={m.produit}>
                {m.raison === 'FORMULE_REQUISE'
                  ? `Aucune formule ${NOMS_PRODUITS[m.produit] ?? m.nom} dans cet espace.`
                  : `La formule ${NOMS_PRODUITS[m.produit] ?? m.nom} de cet espace est complète.`}{' '}
                <Link to={`/abonnements?espace=${encodeURIComponent(manques.spaceId)}&produit=${m.produit.toLowerCase()}`}>
                  {m.raison === 'FORMULE_REQUISE' ? 'Souscrire dans cet espace' : 'Changer de formule'}
                </Link>
              </li>
            ))}
          </ul>
          <p className="dos-aide">Une fois la formule en place, revenez programmer le déplacement.</p>
        </div>
      ) : null}

      {succes ? <p role="status">{succes}</p> : null}
      {erreur ? (
        <p className="dos-erreur" role="alert">
          {erreur}
        </p>
      ) : null}
    </section>
  );
}
